/**
 * Centralized Route Title Resolver
 *
 * Single source of truth for browser tab titles. Called from `AppPages`
 * on every navigation — the returned string is shown as
 * "Buffer - {title}" via `usePageTitle`.
 *
 * To add a new page title, add an entry to `ROUTE_TITLES` below:
 * - Top-level area: add a key with `{ title: 'Area Name' }`
 * - Sub-page: add a key to the area's `children` map
 *
 * Dynamic titles (e.g. feed names) are not handled here — they fall
 * through to the nearest static parent title.
 */

interface RouteConfig {
  title: string
  children?: Record<string, string>
}

const ROUTE_TITLES: Record<string, RouteConfig | undefined> = {
  home: { title: 'Home' },
  create: {
    title: 'Create',
    children: {
      ideas: 'Ideas',
      feeds: 'Feeds',
      templates: 'Templates',
    },
  },
  community: { title: 'Community' },
  insights: { title: 'Insights' },
  settings: {
    title: 'Settings',
    children: {
      channels: 'Channels',
      general: 'General',
      preferences: 'Preferences',
      notifications: 'Notifications',
      billing: 'Billing',
      team: 'Team',
      tags: 'Tags',
      'channel-groups': 'Channel Groups',
      'saved-replies': 'Saved Replies',
      integrations: 'Integrations',
      'apps-extras': 'Apps & Extras',
      'refer-a-friend': 'Refer a Friend',
      'partner-with-buffer': 'Partner Program',
      beta: 'Beta Features',
      api: 'Personal API',
    },
  },
}

export function getProductAreaTitle(pathname: string): string {
  const segments = pathname.split('/').filter(Boolean)
  const [area, second, third] = segments

  if (area === 'schedule') {
    if (second === 'calendar') return 'Calendar'
    return 'Schedule'
  }

  if (area === 'channels') {
    if (second === 'authorization') return 'Channel Authorization'
    if (third === 'calendar') return 'Calendar'
    if (third === 'schedule') return 'Schedule'
    if (third === 'settings') return 'Channel Settings'
    if (third === 'shop-grid') return 'Shop Grid'
    if (third === 'community') return 'Community'
    if (third === 'analytics') return 'Insights'
    return 'Publish'
  }

  const config = ROUTE_TITLES[area]
  if (!config) return 'Publish'

  const childTitle = second ? config.children?.[second] : undefined
  return childTitle ? `${config.title} - ${childTitle}` : config.title
}
